import React, { useCallback, useEffect, useState } from 'react';

interface LoadingSpinnerProps {
  onLoadingComplete: () => void;
}

export function LoadingSpinner({ onLoadingComplete }: LoadingSpinnerProps) {
  const [loadingText, setLoadingText] = useState('INITIALIZING COSMIC FORCES'); // Show immediately
  const [dots, setDots] = useState('');
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState('loading');
  const fullText = 'INITIALIZING COSMIC FORCES';


  // Enhanced mobile detection with performance considerations
  const getMobileInfo = useCallback(() => {
    const isMobile = window.innerWidth <= 768 || 
           /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    const connection = (navigator as Navigator & {
      connection?: { effectiveType?: string; saveData?: boolean };
    }).connection;
    const isSlowConnection = connection && (
      connection.effectiveType === 'slow-2g' ||
      connection.effectiveType === '2g' ||
      connection.saveData
    );

    return { isMobile, isSlowConnection };
  }, []);

  // Optimized image preloading for mobile
  const preloadImages = useCallback(() => {
    const { isMobile, isSlowConnection } = getMobileInfo();
    
    // Use your actual uploaded images
    const baseImageUrls = [
      '/images/logos/cf-logo-spaceman.png',
      '/images/logos/gg-logo-pixel.png',
      '/images/games/goosebumps-game.jpg',
    ];

    // Additional images for desktop/fast connections only
    const additionalImages = [
      '/images/games/goosebumps-night-scares.jpg',
      '/images/logos/republic-logo.png',
      '/images/avatars/dans-gaming.jpg',
      '/favicon.svg'
    ];

    // Use fewer images on mobile/slow connections
    const imageUrls = isSlowConnection ? baseImageUrls.slice(0, 2) : 
                     isMobile ? baseImageUrls : [...baseImageUrls, ...additionalImages];

    const loadImage = (url: string) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        
        // Add mobile-specific loading attributes
        if (isMobile) {
          img.loading = 'eager';
          img.decoding = 'async';
        }
        
        // Shorter timeout for mobile
        const timeout = setTimeout(() => {
          reject(new Error('Image load timeout'));
        }, isMobile ? 2000 : 3000);
        
        img.onload = () => {
          clearTimeout(timeout);
          resolve(url);
        };
        img.onerror = () => {
          clearTimeout(timeout);
          reject(new Error('Image load failed'));
        };
        img.src = url;
      });
    };

    // Use allSettled with shorter timeout on mobile
    Promise.allSettled(imageUrls.map(loadImage))
      .then(() => {
        setImagesLoaded(true);
        setLoadingPhase('assets-loaded');
      })
      .catch(() => {
        // Fail fast on mobile to improve perceived performance
        setTimeout(() => {
          setImagesLoaded(true);
          setLoadingPhase('assets-loaded');
        }, isMobile ? 500 : 1000);
      });
  }, [getMobileInfo]);

  useEffect(() => {
    const { isMobile, isSlowConnection } = getMobileInfo();
    
    // Adaptive loading times based on device and connection
    const baseLoadingTime = isSlowConnection ? 4000 : 5000; // Increased for better visibility
    const mobileExtraTime = isMobile ? (isSlowConnection ? 2000 : 3000) : 1000; // Increased
    const totalLoadingTime = baseLoadingTime + mobileExtraTime;

    // Start optimized image preloading
    preloadImages();

    // Faster typewriter effect on mobile for better perceived performance
    const typeSpeed = isMobile ? 60 : 80;
    let currentIndex = 0;
    const typeInterval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setLoadingText(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typeInterval);
        setTimeout(() => {
          setLoadingPhase('loading-assets');
        }, 300);
      }
    }, typeSpeed);

    // Faster dots animation on mobile
    const dotsSpeed = isMobile ? 400 : 500;
    const dotsInterval = setInterval(() => {
      setDots(prev => {
        if (prev.length >= 3) return '';
        return prev + '.';
      });
    }, dotsSpeed);

    // Ensure minimum loading time to show animation
    const minLoadingTime = 4000; // Minimum 4 seconds to see the animation
    const startTime = Date.now();
    
    // Adaptive completion timing
    const completeTimeout = setTimeout(() => {
      const checkCompletion = () => {
        const elapsedTime = Date.now() - startTime;
        const remainingMinTime = Math.max(0, minLoadingTime - elapsedTime);
        
        if (imagesLoaded || isSlowConnection) {
          setTimeout(() => {
            setLoadingPhase('complete');
            setTimeout(onLoadingComplete, 300);
          }, remainingMinTime);
        } else {
          setTimeout(checkCompletion, 300);
        }
      };
      checkCompletion();
    }, Math.max(totalLoadingTime, minLoadingTime));

    return () => {
      clearInterval(typeInterval);
      clearInterval(dotsInterval);
      clearTimeout(completeTimeout);
    };
  }, [getMobileInfo, onLoadingComplete, imagesLoaded, preloadImages]);

  const getLoadingMessage = () => {
    switch (loadingPhase) {
      case 'initializing':
        return `LOADING${dots}`;
      case 'loading-assets':
        return `OPTIMIZING${dots}`;
      case 'assets-loaded':
        return `SYSTEMS READY${dots}`;
      case 'complete':
        return 'LAUNCH READY';
      default:
        return `LOADING${dots}`;
    }
  };

  // Mobile detection for responsive animation timing
  const isMobile = typeof window !== 'undefined' && (
    window.innerWidth <= 768 || 
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  );

  // Animation durations - much slower on mobile for better visual experience
  const animationSpeeds = {
    outerRing: isMobile ? '8s' : '2s',        // Outer cyan ring (4x slower)
    middleRing: isMobile ? '6s' : '1.5s',     // Middle purple ring (4x slower)
    innerRing: isMobile ? '4s' : '1s',        // Inner orange ring (4x slower)
    pulse: isMobile ? '6s' : '2s',            // Center core pulse (3x slower)
    backgroundPulse1: isMobile ? '10s' : '3s', // Background orb 1 (3x slower)
    backgroundPulse2: isMobile ? '12s' : '4s', // Background orb 2 (3x slower)
    progress: isMobile ? '8s' : '4s'          // Progress bar (2x slower)
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: '#0f172a',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 99999,
      overflow: 'hidden'
    }}>
      {/* Cyberpunk background effects */}
      <div style={{
        position: 'absolute',
        top: '25%',
        left: '25%',
        width: '200px',
        height: '200px',
        background: 'radial-gradient(circle, rgba(34,211,238,0.1) 0%, transparent 70%)',
        borderRadius: '50%',
        animation: `pulse ${animationSpeeds.backgroundPulse1} ease-in-out infinite`
      }}></div>
      <div style={{
        position: 'absolute',
        bottom: '25%',
        right: '25%',
        width: '250px',
        height: '250px',
        background: 'radial-gradient(circle, rgba(168,85,247,0.1) 0%, transparent 70%)',
        borderRadius: '50%',
        animation: `pulse ${animationSpeeds.backgroundPulse2} ease-in-out infinite reverse`
      }}></div>

      <div style={{
        textAlign: 'center',
        color: 'white',
        position: 'relative',
        zIndex: 10
      }}>
        {/* Cyberpunk multi-ring spinner */}
        <div style={{
          position: 'relative',
          width: '120px',
          height: '120px',
          margin: '0 auto 40px auto'
        }}>
          {/* Outer ring - Cyan */}
          <div style={{
            position: 'absolute',
            width: '120px',
            height: '120px',
            border: '4px solid transparent',
            borderTop: '4px solid #22d3ee',
            borderRadius: '50%',
            animation: `spin ${animationSpeeds.outerRing} linear infinite`
          }}></div>
          
          {/* Middle ring - Purple */}
          <div style={{
            position: 'absolute',
            top: '15px',
            left: '15px',
            width: '90px',
            height: '90px',
            border: '3px solid transparent',
            borderTop: '3px solid #a855f7',
            borderRadius: '50%',
            animation: `spin-reverse ${animationSpeeds.middleRing} linear infinite`
          }}></div>
          
          {/* Inner ring - Orange */}
          <div style={{
            position: 'absolute',
            top: '30px',
            left: '30px',
            width: '60px',
            height: '60px',
            border: '2px solid transparent',
            borderTop: '2px solid #f97316',
            borderRadius: '50%',
            animation: `spin ${animationSpeeds.innerRing} linear infinite`
          }}></div>
          
          {/* Center core */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '20px',
            height: '20px',
            backgroundColor: '#22d3ee',
            borderRadius: '50%',
            animation: `pulse ${animationSpeeds.pulse} ease-in-out infinite`,
            boxShadow: '0 0 20px #22d3ee'
          }}></div>
        </div>
        
        {/* Loading text with cyberpunk styling */}
        <div style={{
          fontSize: '28px',
          color: '#22d3ee',
          marginBottom: '20px',
          fontFamily: 'monospace',
          fontWeight: 'bold',
          textShadow: '0 0 10px #22d3ee',
          letterSpacing: '3px'
        }}>
          {loadingText || 'COSMIC FORCES LOADING'}
        </div>
        
        {/* Status with glowing effect */}
        <div style={{
          fontSize: '18px',
          color: '#a855f7',
          marginBottom: '30px',
          fontFamily: 'monospace',
          textShadow: '0 0 8px #a855f7'
        }}>
          {getLoadingMessage()}
        </div>
        
        {/* Cyberpunk progress bar */}
        <div style={{
          width: '320px',
          height: '12px',
          backgroundColor: '#1e293b',
          margin: '0 auto',
          borderRadius: '6px',
          overflow: 'hidden',
          border: '1px solid #22d3ee',
          boxShadow: '0 0 10px rgba(34,211,238,0.3)'
        }}>
          <div style={{
            height: '100%',
            background: 'linear-gradient(90deg, #22d3ee 0%, #a855f7 50%, #f97316 100%)',
            animation: `loading-progress ${animationSpeeds.progress} ease-out forwards`,
            boxShadow: '0 0 10px rgba(34,211,238,0.5)'
          }}></div>
        </div>

        {/* System status indicators */}
        <div style={{
          marginTop: '30px',
          fontSize: '14px',
          color: '#64748b',
          fontFamily: 'monospace'
        }}>
          <div style={{ marginBottom: '5px' }}>◦ INITIALIZING SYSTEMS</div>
          <div style={{ marginBottom: '5px' }}>◦ LOADING ASSETS</div>
          <div>◦ PREPARING INTERFACE</div>
        </div>
      </div>
      
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes spin-reverse {
          from { transform: rotate(360deg); }
          to { transform: rotate(0deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.05); }
        }
        @keyframes loading-progress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </div>
  );
}